import { create } from "zustand";
import { produce } from "immer";

import { FAIXAS_DEFAULT } from "./constants";
import { applyCrmPetContactEvent } from "../../services/formatResponses/crmPet";

export const CRM_PET_DEFAULT_FILTER = {
  search: "",
  rule: "ABSENT",
  minDays: "30",
  contact: "ALL",
  subscriber: "ALL",
};

export const CRM_PET_DEFAULT_OPPORTUNITIES_FILTER = {
  from: "",
  to: "",
};

export const CRM_PET_DEFAULT_PENDING_FILTER = {
  from: "",
  to: "",
};

export const CRM_PET_DEFAULT_CONTACTED_FILTER = {
  from: "",
  to: "",
  diasFiltro: "30",
  customDays: "30",
};

const defaultData = {
  isLoading: false,
  isWaiting: false,
  error: null,
  customers: [],
  appliedContactEvents: {},
  config: {
    faixas: FAIXAS_DEFAULT,
    rules: [],
  },
  selectedCustomerKey: null,
  whatsapp: { loading: false, error: null, customer: null },
  contactSubmitting: false,
  pets: [],
  opportunities: {
    isLoading: false,
    isWaiting: false,
    error: null,
    pets: [],
    filter: CRM_PET_DEFAULT_OPPORTUNITIES_FILTER,
    pagination: {
      page: 0,
      totalPages: 1,
      perPage: 10,
      totalDocuments: 0,
    },
  },
  pending: {
    isLoading: false,
    isWaiting: false,
    error: null,
    pets: [],
    filter: CRM_PET_DEFAULT_PENDING_FILTER,
    pagination: {
      page: 0,
      totalPages: 1,
      perPage: 10,
      totalDocuments: 0,
    },
  },
  contacted: {
    isLoading: false,
    isWaiting: false,
    error: null,
    pets: [],
    filter: CRM_PET_DEFAULT_CONTACTED_FILTER,
    pagination: {
      page: 0,
      totalPages: 1,
      perPage: 10,
      totalDocuments: 0,
    },
  },
  faixas: FAIXAS_DEFAULT,
  showConfig: false,
  whatsappPetId: null,
  contactDone: {},
  contactMarking: {},
  petBlocking: {},
  contactHistory: {
    show: false,
    loading: false,
    pet: null,
    items: [],
  },
  filter: CRM_PET_DEFAULT_FILTER,
  pagination: {
    page: 0,
    totalPages: 1,
    perPage: 10,
    totalDocuments: 0,
  },
  sort: {
    sortBy: "customerName",
    sortOrder: "asc",
  },
  summary: {
    totalAll: 0,
    realizados: 0,
    pendentes: 0,
    bloqueados: 0,
  },
};

export const useCrmPet = create((set) => ({
  data: defaultData,
  actions: {
    setWhatsApp: (whatsapp) => set((state) => ({ data: { ...state.data, whatsapp } })),
    setLoading: (isLoading) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.isLoading = isLoading;
          draft.data.isWaiting = isLoading;
        })
      ),
    setWaiting: (isWaiting) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.isWaiting = isWaiting;
        })
      ),
    setError: (error) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.error = error;
        })
      ),
    setOpportunitiesLoading: (isLoading) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.opportunities.isLoading = isLoading;
          draft.data.opportunities.isWaiting = isLoading;
        })
      ),
    setOpportunitiesWaiting: (isWaiting) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.opportunities.isWaiting = isWaiting;
        })
      ),
    setOpportunitiesError: (error) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.opportunities.error = error;
        })
      ),
    setOpportunitiesFilter: (filterData) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.opportunities.filter = {
            ...state.data.opportunities.filter,
            ...filterData,
          };
        })
      ),
    setPendingLoading: (isLoading) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pending.isLoading = isLoading;
          draft.data.pending.isWaiting = isLoading;
        })
      ),
    setPendingWaiting: (isWaiting) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pending.isWaiting = isWaiting;
        })
      ),
    setPendingError: (error) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pending.error = error;
        })
      ),
    setPendingFilter: (filterData) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pending.filter = {
            ...state.data.pending.filter,
            ...filterData,
          };
        })
      ),
    setContactedLoading: (isLoading) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contacted.isLoading = isLoading;
          draft.data.contacted.isWaiting = isLoading;
        })
      ),
    setContactedWaiting: (isWaiting) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contacted.isWaiting = isWaiting;
        })
      ),
    setContactedError: (error) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contacted.error = error;
        })
      ),
    setContactedFilter: (filterData) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contacted.filter = {
            ...state.data.contacted.filter,
            ...filterData,
          };
        })
      ),
    setFilter: (filterData) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.filter = { ...state.data.filter, ...filterData };
        })
      ),
    setCustomers: (customers, pagination, summary) =>
      set((state) =>
        produce(state, (draft) => {
          const page = pagination.page || 0;
          draft.data.customers[page] = customers;
          draft.data.pagination = pagination;
          draft.data.summary = summary;
          draft.data.isLoading = false;
          draft.data.isWaiting = false;
          draft.data.error = null;
        })
      ),
    applyContactEvent: (event) =>
      set((state) =>
        produce(state, (draft) => {
          if (!event?._id || draft.data.appliedContactEvents[event._id]) return;
          const updatedRows = new Set();
          draft.data.customers.forEach((rows) => {
            rows.forEach((row, index) => {
              const updated = applyCrmPetContactEvent(row, event);
              if (updated === row) return;
              rows[index] = updated;
              const key = row.rowKey || `${row.customerId}:${row.pet.petId}`;
              if (updatedRows.has(key)) return;
              updatedRows.add(key);
              const wasContacted = row.contactSummary?.status === "CONTACTED";
              const isContacted = updated.contactSummary.status === "CONTACTED";
              if (wasContacted !== isContacted) {
                const delta = isContacted ? 1 : -1;
                draft.data.summary.realizados += delta;
                draft.data.summary.pendentes -= delta;
              }
            });
          });
          draft.data.appliedContactEvents[event._id] = true;
        })
      ),
    clearCustomers: ({ preserveWhatsApp = false } = {}) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.customers = [];
          draft.data.appliedContactEvents = {};
          if (!preserveWhatsApp) {
            draft.data.selectedCustomerKey = null;
            draft.data.whatsapp = { loading: false, error: null, customer: null };
          }
          draft.data.petBlocking = {};
        })
      ),
    setConfig: (config) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.config = config;
          draft.data.faixas = config.faixas?.length ? config.faixas : FAIXAS_DEFAULT;
        })
      ),
    setSelectedCustomerKey: (selectedCustomerKey) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.selectedCustomerKey = selectedCustomerKey;
        })
      ),
    setContactSubmitting: (contactSubmitting) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contactSubmitting = contactSubmitting;
        })
      ),
    setPets: (pets, pagination, summary = null) =>
      set((state) =>
        produce(state, (draft) => {
          const page = pagination.page || 0;
          const contactDone = pets.reduce((result, pet) => {
            result[pet.petId] = Boolean(pet.contatoRealizado);
            return result;
          }, {});

          draft.data.pets[page] = pets;
          draft.data.contactDone = {
            ...draft.data.contactDone,
            ...contactDone,
          };
          draft.data.pagination = pagination;
          if (summary) draft.data.summary = summary;
          draft.data.isLoading = false;
          draft.data.isWaiting = false;
          draft.data.error = null;
        })
      ),
    setOpportunitiesPets: (pets, pagination) =>
      set((state) =>
        produce(state, (draft) => {
          const page = pagination.page || 0;
          const contactDone = pets.reduce((result, pet) => {
            result[pet.petId] = Boolean(pet.contatoRealizado);
            return result;
          }, {});

          draft.data.opportunities.pets[page] = pets;
          draft.data.contactDone = {
            ...draft.data.contactDone,
            ...contactDone,
          };
          draft.data.opportunities.pagination = pagination;
          draft.data.opportunities.isLoading = false;
          draft.data.opportunities.isWaiting = false;
          draft.data.opportunities.error = null;
        })
      ),
    setPendingPets: (pets, pagination) =>
      set((state) =>
        produce(state, (draft) => {
          const page = pagination.page || 0;
          const contactDone = pets.reduce((result, pet) => {
            result[pet.petId] = Boolean(pet.contatoRealizado);
            return result;
          }, {});

          draft.data.pending.pets[page] = pets;
          draft.data.contactDone = {
            ...draft.data.contactDone,
            ...contactDone,
          };
          draft.data.pending.pagination = pagination;
          draft.data.pending.isLoading = false;
          draft.data.pending.isWaiting = false;
          draft.data.pending.error = null;
        })
      ),
    setContactedPets: (pets, pagination) =>
      set((state) =>
        produce(state, (draft) => {
          const page = pagination.page || 0;
          const contactDone = pets.reduce((result, pet) => {
            result[pet.petId] = Boolean(pet.contatoRealizado);
            return result;
          }, {});

          draft.data.contacted.pets[page] = pets;
          draft.data.contactDone = {
            ...draft.data.contactDone,
            ...contactDone,
          };
          draft.data.contacted.pagination = pagination;
          draft.data.contacted.isLoading = false;
          draft.data.contacted.isWaiting = false;
          draft.data.contacted.error = null;
        })
      ),
    clearPets: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pets = [];
          draft.data.contactDone = {};
          draft.data.contactMarking = {};
          draft.data.petBlocking = {};
        })
      ),
    clearOpportunitiesPets: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.opportunities.pets = [];
        })
      ),
    clearPendingPets: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pending.pets = [];
        })
      ),
    clearContactedPets: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contacted.pets = [];
        })
      ),
    setPaginationPage: (page) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pagination.page = page;
        })
      ),
    setOpportunitiesPaginationPage: (page) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.opportunities.pagination.page = page;
        })
      ),
    setPendingPaginationPage: (page) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pending.pagination.page = page;
        })
      ),
    setContactedPaginationPage: (page) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contacted.pagination.page = page;
        })
      ),
    setSort: (sortBy, sortOrder) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.sort.sortBy = sortBy;
          draft.data.sort.sortOrder = sortOrder || "asc";
        })
      ),
    setFaixas: (faixas) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.faixas = faixas?.length ? faixas : FAIXAS_DEFAULT;
        })
      ),
    setShowConfig: (showConfig) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.showConfig = showConfig;
        })
      ),
    setWhatsappPetId: (whatsappPetId) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.whatsappPetId = whatsappPetId;
        })
      ),
    setContactMarking: (petId, marking) =>
      set((state) =>
        produce(state, (draft) => {
          if (marking) draft.data.contactMarking[petId] = true;
          else delete draft.data.contactMarking[petId];
        })
      ),
    setPetContactDone: (petId, done, dataContato = null) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contactDone[petId] = done;
          draft.data.pets.forEach((pagePets) => {
            pagePets.forEach((pet) => {
              if (pet.petId === petId) {
                pet.contatoRealizado = done;
                pet.dataContato = dataContato;
              }
            });
          });
          draft.data.opportunities.pets.forEach((pagePets) => {
            pagePets.forEach((pet) => {
              if (pet.petId === petId) {
                pet.contatoRealizado = done;
                pet.dataContato = dataContato;
              }
            });
          });
          draft.data.pending.pets.forEach((pagePets) => {
            pagePets.forEach((pet) => {
              if (pet.petId === petId) {
                pet.contatoRealizado = done;
                pet.dataContato = dataContato;
              }
            });
          });
          draft.data.contacted.pets.forEach((pagePets) => {
            pagePets.forEach((pet) => {
              if (pet.petId === petId) {
                pet.contatoRealizado = done;
                pet.dataContato = dataContato;
              }
            });
          });
        })
      ),
    setPetBlocking: (petId, blocking) =>
      set((state) =>
        produce(state, (draft) => {
          if (blocking) draft.data.petBlocking[petId] = true;
          else delete draft.data.petBlocking[petId];
        })
      ),
    setPetBlocked: (petId, bloqueado, dataBloqueio = null) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.customers.forEach((pageCustomers) => {
            pageCustomers.forEach((customer) => {
              customer.pets.forEach((pet) => {
                if (pet.petId === petId) {
                  pet.blocked = bloqueado;
                  pet.blockedAt = dataBloqueio;
                }
              });
            });
          });
          draft.data.pets.forEach((pagePets) => {
            pagePets.forEach((pet) => {
              if (pet.petId === petId) {
                pet.bloqueado = bloqueado;
                pet.dataBloqueio = dataBloqueio;
              }
            });
          });
          draft.data.opportunities.pets.forEach((pagePets) => {
            pagePets.forEach((pet) => {
              if (pet.petId === petId) {
                pet.bloqueado = bloqueado;
                pet.dataBloqueio = dataBloqueio;
              }
            });
          });
          draft.data.pending.pets.forEach((pagePets) => {
            pagePets.forEach((pet) => {
              if (pet.petId === petId) {
                pet.bloqueado = bloqueado;
                pet.dataBloqueio = dataBloqueio;
              }
            });
          });
          draft.data.contacted.pets.forEach((pagePets) => {
            pagePets.forEach((pet) => {
              if (pet.petId === petId) {
                pet.bloqueado = bloqueado;
                pet.dataBloqueio = dataBloqueio;
              }
            });
          });
        })
      ),
    openContactHistory: (customer) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contactHistory.show = true;
          draft.data.contactHistory.loading = true;
          draft.data.contactHistory.pet = customer;
          draft.data.contactHistory.items = [];
        })
      ),
    setContactHistory: (items) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contactHistory.items = items;
          draft.data.contactHistory.loading = false;
        })
      ),
    closeContactHistory: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contactHistory = defaultData.contactHistory;
        })
      ),
    clearFilter: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.filter = CRM_PET_DEFAULT_FILTER;
        })
      ),
    clearOpportunitiesFilter: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.opportunities.filter =
            CRM_PET_DEFAULT_OPPORTUNITIES_FILTER;
        })
      ),
    clearPendingFilter: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.pending.filter = CRM_PET_DEFAULT_PENDING_FILTER;
        })
      ),
    clearContactedFilter: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.contacted.filter = CRM_PET_DEFAULT_CONTACTED_FILTER;
        })
      ),
    clear: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data = defaultData;
        })
      ),
  },
}));
